import React, { useState, useMemo, useEffect, useRef } from 'react';
import useSelection from '../../../hooks/useSelection';
import './MarketCyclesPage.css';
import { useGeographicUnits } from '../../../hooks/useGeographicUnits';
import { useIndicatorSeries } from '../../../hooks/useIndicatorSeries';
import { useAuth } from '../../../context/AuthContext';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import jsPDF from "jspdf";
import templateBg from "../../../assets/images/market_cycles_template.png";
import portadaReporte from "../../../assets/images/portada_reporte_invescasa.png";
import pagina1Invescasa from "../../../assets/images/que_es_invescasa_pagina_1.png";
import pagina2Invescasa from "../../../assets/images/que_es_invescasa_pagina_2.png";
import html2canvas from "html2canvas";

import useIndicatorYearRanges from "../../../hooks/useIndicatorYearRanges";


/* Palette aligned with Variables.css */
const colors = [
  '#DFA135', // chart-1
  '#E12020', // chart-2
  '#00A4E4', // chart-3
  '#34BF49', // chart-4
  '#04519E', // chart-5
  '#F47721', // chart-6
  '#D20962', // chart-7
  '#7D3F98', // chart-8
];



export default function MarketCyclesPage({ level }) {
  const { places, loading } = useGeographicUnits(level);
  const { membership } = useAuth();
  const [multiMode, setMultiMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const smallChartRef = useRef(null);
  const tableRef = useRef(null);

  const getTableImage = async () => {
  if (!tableRef.current) {
    console.warn("tableRef.current es null: la tabla aún no está montada");
    return null;
  }

  const canvas = await html2canvas(tableRef.current, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
  });

  return canvas.toDataURL("image/png");
};


  const getSmallChartImage = async () => {
  if (!smallChartRef.current) {
    throw new Error("No se encontró el contenedor de la gráfica pequeña");
  }

  // 1. Buscar el <svg> que dibuja Recharts
  const svg = smallChartRef.current.querySelector("svg.recharts-surface");

  if (!svg) {
    throw new Error("No se encontró el SVG dentro de la gráfica pequeña");
  }

  // 2. Serializar el SVG a texto
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);

  // 3. Crear un Blob y una URL temporal
  const svgBlob = new Blob([svgString], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(svgBlob);

  // 4. Convertir el SVG en PNG usando <img> + <canvas>
  const imgData = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const rect = svg.getBoundingClientRect();
      const scale = 2;
      const width = rect.width * scale;
      const height = rect.height * scale;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("No se pudo obtener el contexto del canvas"));
        return;
      }

      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.drawImage(img, 0, 0);

      const dataUrl = canvas.toDataURL("image/png");
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };

    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    img.src = url;
  });

  return imgData;
};
  



  const handleDownloadPDF = async () => {
  try{


  
  setIsGenerating(true);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [1920, 1080],
  }); 

  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // ============================
  // LOAD PORTADA IMAGES
  // ============================
  const portada1 = new Image();
  portada1.src = portadaReporte;
  portada1.crossOrigin = "anonymous";

  const portada2 = new Image();
  portada2.src = pagina1Invescasa;

  const portada3 = new Image();
  portada3.src = pagina2Invescasa;

  await Promise.all([
    new Promise(r => portada1.onload = r),
    new Promise(r => portada2.onload = r),
    new Promise(r => portada3.onload = r),
  ]);

  // ============================
  // LOAD TEMPLATE BACKGROUND
  // ============================
  const templateImg = new Image();
  templateImg.src = templateBg;
  templateImg.crossOrigin = "anonymous";
  await new Promise((resolve) => (templateImg.onload = resolve));

  // ============================
  // PORTADAS
  // ============================
  pdf.addImage(portada1, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(42);
  pdf.setTextColor("#ffffff");
  pdf.text("Market cycles by states", 370, 685);

  pdf.addPage();
  pdf.addImage(portada2, "PNG", 0, 0, pageWidth, pageHeight);

  pdf.addPage();
  pdf.addImage(portada3, "PNG", 0, 0, pageWidth, pageHeight);

  // ============================
  //  TABLE OF CONTENTS 
  // ============================
  pdf.addPage();
  pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

  // TITLE
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(125);
  pdf.setTextColor("#FFFFFF");
  pdf.text("Table of Contents", 450, 145);

  // LIST
  const items = ["Wealth advantage: Market cycles by states"];
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(38);
  pdf.setTextColor("#0f385d");

  const startX = 450;
  const baseY = 360;
  const stepY = 65;

  items.forEach((item, i) => {
    pdf.text(`${i + 1}. ${item}`, startX, baseY + i * stepY);
  });

  // RIGHT BAR
  pdf.setFillColor("#0f385d");
  pdf.rect(1550, baseY - 38, 11, (items.length - 1) * stepY + 53, "F");

  // ============================
  // GRAPH PAGE
  // ============================
  if (yearsLoading || loadingChart) {
    console.warn("Aún están cargando los años o la gráfica");
    return;
  }

  pdf.addPage();
  pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

  // 1) Obtener título de la gráfica desde el DOM
  let titleText = "Mean house price ($) per year"; // valor por defecto

  if (smallChartRef.current) {
    const titleEl = smallChartRef.current.querySelector(".chart-title");
    if (titleEl && titleEl.innerText) {
      titleText = titleEl.innerText;
    }
  }

  // 2) Dibujar el título en el PDF
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(32);
  pdf.setTextColor("#000000");
  pdf.text(titleText, 200, 290);

  // 3) Obtener la imagen de la gráfica pequeña
  const imgData = await getSmallChartImage();
  if (!imgData) {
    console.warn("No se pudo generar la imagen de la gráfica");
  } else {
    const imgWidth = 1700;
    const imgHeight = 560;

    pdf.addImage(imgData, "PNG", 130, 300, imgWidth, imgHeight);
  }


  // ============================
  // ============================
  // LEGEND HORIZONTAL with DOTS
  // ============================

  const legendStartX = 250;   // posición inicial X
  const legendY = 240;        // posición fija Y
  const spacing = 190;        // espacio entre items
  const dotRadius = 5;        // tamaño del punto (radio)

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(23);
 
  uniquePlaces.slice().reverse().forEach((place, index) => {
    const color = colorMap[place] || "#000000";
    const x = legendStartX + index * spacing;

    // 🎯 1. Dibujar circulito de color
    pdf.setFillColor(color);
    pdf.circle(x - 10, legendY - 5, dotRadius, "F");

    // 🎯 2. Dibujar texto del estado
    pdf.setTextColor(color);
    pdf.text(place, x, legendY);
  });

  // Regresar color del texto a un valor normal
  pdf.setTextColor("#FFFFFF");


  // Texto AUD Australina Dolar
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(23);
  pdf.setTextColor("#5f5f5f");
  pdf.text("$ AUD", 180,320 );


  // GRAPH TITLES
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(48);
  pdf.setTextColor("#FFFFFF");
  pdf.text("Wealth advantage:", 435, 90);

  pdf.setFont("helvetica", "bold");
  pdf.text("Market cycles by states", 755, 90);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(32);
  pdf.text(
    `Median house price over time by ${isCity ? "city" : "state"}; shows how property values are trending.`,
    435,
    130
  );

  // PAGE NUMBER
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(130);
  pdf.setTextColor("#000000");
  pdf.text("1", 54, 302);

    // ============================
  // TABLE PAGE
  // ============================
  const tableImgData = await getTableImage();

  if (tableImgData) {
    pdf.addPage();
    pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

    // Título de la página de tabla (puedes ajustar el texto)
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(48);
    pdf.setTextColor("#FFFFFF");
    pdf.text("Wealth advantage:", 435, 90);

    pdf.setFont("helvetica", "bold");
    pdf.text("Market cycles – table view", 755, 90);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(32);
    pdf.text(
      "Summary of mean prices and growth by state.",
      435,
      130
    );

    // Imagen de la tabla
    const tableWidth = 1670;
    const tableHeight = 560;
    const tableX = 200;
    const tableY = 280;

    pdf.addImage(tableImgData, "PNG", tableX, tableY, tableWidth, tableHeight);

    // PAGE NUMBER (página 2)
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(130);
    pdf.setTextColor("#000000");
    pdf.text("2", 54, 302);
  }


  // SAVE PDF
  pdf.save("market-report.pdf");

}catch (err) {
    console.error("Error generating PDF:", err);
  } finally {
    setIsGenerating(false); 
  }
};


  useEffect(() => {
    const status = membership?.status || '';
    const active = String(status).toLowerCase() === 'active';
    setMultiMode(active);
  }, [membership]);

  const { selected, selectedPlaces, handleClick, setSelected } = useSelection({ 
    multi: multiMode, 
    defaultValue: places && places.length > 0 ? places[0] : 'All', 
    places 
  });

  useEffect(() => {
    if (places && places.length > 0) {
      setSelected(multiMode ? ['All'] : places[0]);
    }
  }, [multiMode, places, setSelected]);

  const isCity = level === 'City';
  const unitLabelPlural = isCity ? 'Cities' : 'States';
  const unitLabelSingular = isCity ? 'City' : 'State';

  const indicatorName = isCity ? 'house_price_city' : 'median_house_price';

  const { yearRanges, loading: yearsLoading } = useIndicatorYearRanges([indicatorName]);

  const years = useMemo(() => {
    const range = yearRanges[indicatorName] || [2000, new Date().getFullYear()];
    return Array.from(
      { length: range[1] - range[0] + 1 },
      (_, i) => range[0] + i
    );
  }, [yearRanges, indicatorName]);

  // ==== Modal state (expanded chart) ====
  const [showChartModal, setShowChartModal] = useState(false);
  const openChartModal = () => {
    setShowChartModal(true);
    document.body.classList.add('mc-modal-open');
  };
  const closeChartModal = () => {
    setShowChartModal(false);
    document.body.classList.remove('mc-modal-open');
  };
  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeChartModal();
  };
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && closeChartModal();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  const safeYears = useMemo(() => (yearsLoading ? [] : years), [yearsLoading, years]);

  const { data: seriesData, loading: loadingChart } =
    useIndicatorSeries(indicatorName, places, safeYears);

  const colorMap = useMemo(() => {
    const map = {};
    (places || []).forEach((p, i) => (map[p] = colors[i % colors.length]));
    return map;
  }, [places]);

  const uniquePlaces = useMemo(
    () => (places || []).filter(p => selectedPlaces?.includes(p) && !!seriesData && !!seriesData[p]),
    [places, selectedPlaces, seriesData]
  );

  const tableRows = useMemo(() => {
    return (uniquePlaces || []).map((place) => {
      const p2014 = seriesData[place]?.find(d => d.year === 2014)?.average;
      const p2020 = seriesData[place]?.find(d => d.year === 2020)?.average;
      const p2024 = seriesData[place]?.find(d => d.year === 2024)?.average;
      const g5 = p2014 && p2020 ? ((p2020 - p2014) / p2014) * 100 : null;
      const g10 = p2014 && p2024 ? ((p2024 - p2014) / p2014) * 100 : null;
      return { place, color: colorMap[place], '2014': p2014, '2020': p2020, '2024': p2024, growth5y: g5, growth10y: g10 };
    });
  }, [uniquePlaces, seriesData, colorMap]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return tableRows;
    const rows = [...tableRows];
    rows.sort((a, b) => {
      if (sortConfig.key === 'state') {
        const A = a.place?.toLowerCase() ?? '';
        const B = b.place?.toLowerCase() ?? '';
        return sortConfig.direction === 'asc' ? A.localeCompare(B) : B.localeCompare(A);
      }
      const A = a[sortConfig.key] ?? -Infinity;
      const B = b[sortConfig.key] ?? -Infinity;
      return sortConfig.direction === 'asc' ? A - B : B - A;
    });
    return rows;
  }, [tableRows, sortConfig]);

  function handleSort(key) {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' }
    );
  }

  const items = loading ? ['All'] : ['All', ...[...(places || [])].sort()];

  const chartSeries = useMemo(() => {
    const result = [];
    if (!seriesData) return result;
    for (const place of uniquePlaces) {
      const series = seriesData[place];
      if (!series) continue;
      for (const pt of series) {
        let row = result.find(d => d.year === pt.year);
        if (!row) { row = { year: pt.year }; result.push(row); }
        row[place] = pt.average;
      }
    }
    return result.sort((a, b) => a.year - b.year);
  }, [seriesData, uniquePlaces]);

  const yTicks = useMemo(() => {
    let min = Infinity, max = -Infinity;
    for (const row of chartSeries) {
      for (const place of uniquePlaces) {
        const v = row[place];
        if (typeof v === 'number') {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    if (!isFinite(min) || !isFinite(max)) return [];

    const STEP = 200_000;
    const lo = Math.floor(min / STEP) * STEP;
    const hi = Math.ceil(max / STEP) * STEP;

    const ticks = [];
    for (let v = lo; v <= hi; v += STEP) ticks.push(v);
    return ticks;
  }, [chartSeries, uniquePlaces]);

  const SortIcon = ({ active, direction }) => (
    <svg className={`sort-main-icon${active ? ' sort-icon-active' : ''}`} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M6 11L9 14L12 11" stroke={active && direction === 'desc' ? "#3857e0" : "#B1B1B1"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 7L9 4L6 7" stroke={active && direction === 'asc' ? "#3857e0" : "#B1B1B1"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="background">
      <div className="panel-modal-container">

        {/* 1) TITLE BAR (unchanged structure) */}
        <div className="main-title">
          <h2 className="main-title__title">
            {isCity ? (
              <>
                <strong>Wealth advantage:</strong> Market cycles by cities
              </>
            ) : (
              <>
                <strong>Wealth advantage:</strong> Market cycles by states
              </>
            )}
          </h2>
              <button 
                className="download-report" 
                aria-label="Download report" 
                onClick={handleDownloadPDF}
                disabled={!multiMode || isGenerating || yearsLoading || loadingChart}
                style={!multiMode || isGenerating || yearsLoading || loadingChart ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <span className="icon" aria-hidden>
                  {isGenerating ? (
                    <span className="spinner"></span>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                </span>

                {isGenerating ? "Generating..." : "Download report"}
              </button>

        </div>

        {/* 2) LABEL + TABS (unchanged structure) */}
        <div className="mini-banner">
          <span className="banner-label">{unitLabelPlural}</span>
          <div className="tabs">
            {items.map((item) => {
              const isActive = Array.isArray(selected) ? selected.includes(item) : selected === item;
              const isAll = item === 'All';
              const isMultiMode = Array.isArray(selected);
              const color = !isAll ? colorMap[item] : '#092463';

              return (
                <button
                  key={item}
                  className={`tab-button${isActive ? ' active' : ''}${isAll && !isMultiMode ? ' disabled' : ''}`}
                  onClick={() => (isAll && !isMultiMode) ? null : handleClick(item)}
                  aria-pressed={isActive}
                  disabled={isAll && !isMultiMode}
                  style={
                    isActive && !isAll
                      ? { borderColor: color, background: 'var(--color-blue-040)' }
                      : undefined
                  }
                >
                  <span
                    className={`tab-radio${isActive ? ' active' : ''}`}
                    style={
                      !isAll
                        ? { borderColor: isActive ? color : 'var(--color-navy)', color: isActive ? color : undefined }
                        : undefined
                    }
                    aria-hidden
                  />
                  <span className="tab-label">{item}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 3) CONTENT */}
        <div className="panel-modal">
          <div className="chart-wrapper" ref={smallChartRef}>
            <div className="chart-title">
              Mean house price ($) per year
              {/* Info icon triggers expanded modal */}
                  <button
                    className="info-trigger"
                    type="button"
                    aria-label="Expand chart"
                    onClick={openChartModal}
                  >
                    <svg
                      className="info-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="4 9 4 4 9 4" />
                      <line x1="4" y1="4" x2="10" y2="10" />

                      <polyline points="15 4 20 4 20 9" />
                      <line x1="20" y1="4" x2="14" y2="10" />

                      <polyline points="20 15 20 20 15 20" />
                      <line x1="20" y1="20" x2="14" y2="14" />

                      <polyline points="9 20 4 20 4 15" />
                      <line x1="4" y1="20" x2="10" y2="14" />
                    </svg>
                  </button>
            </div>

            {yearsLoading ? (
              <div className="chart-placeholder">Loading years...</div>
            ) : loadingChart ? (
              <div className="chart-placeholder">Loading chart...</div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartSeries} margin={{ top: 20, right: 48, left: 20, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" height={28} tickMargin={12} />
                  <YAxis
                    tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)} mill.`}
                    ticks={yTicks}
                    domain={yTicks.length ? [yTicks[0], yTicks[yTicks.length - 1]] : ['auto', 'auto']}
                    interval={0}
                    minTickGap={8}
                    padding={{ top: 16, bottom: 8 }}
                    tickMargin={10}
                    allowDecimals
                  />
                  <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  {uniquePlaces.map((place) => (
                    <Line
                      key={place}
                      type="monotone"
                      dataKey={place}
                      stroke={colorMap[place]}
                      dot={false}
                      activeDot={{ r: 5 }}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="table-wrapper" ref={tableRef}>
            <table className="table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('state')}>
                    {unitLabelSingular}
                    <SortIcon active={sortConfig.key === 'state'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => handleSort('2014')}>Mean price 2014<SortIcon active={sortConfig.key === '2014'} direction={sortConfig.direction} /></th>
                  <th onClick={() => handleSort('2020')}>Mean price 2020<SortIcon active={sortConfig.key === '2020'} direction={sortConfig.direction} /></th>
                  <th onClick={() => handleSort('2024')}>Mean price 2024<SortIcon active={sortConfig.key === '2024'} direction={sortConfig.direction} /></th>
                  <th onClick={() => handleSort('growth5y')}>Growth (%) 5 years<SortIcon active={sortConfig.key === 'growth5y'} direction={sortConfig.direction} /></th>
                  <th onClick={() => handleSort('growth10y')}>Growth (%) 10 years<SortIcon active={sortConfig.key === 'growth10y'} direction={sortConfig.direction} /></th>
                </tr>
              </thead>
              <tbody>
                {(sortConfig.key ? sortedRows : tableRows).map((row) => (
                  <tr key={row.place}>
                    <td className="state-cell-modern">
                      <span className="state-color-modern" style={{ backgroundColor: row.color }} />
                      <span className="state-name">{row.place}</span>
                    </td>
                    <td>{row['2014'] ? `$${row['2014'].toLocaleString()}` : "-"}</td>
                    <td>{row['2020'] ? `$${row['2020'].toLocaleString()}` : "-"}</td>
                    <td>{row['2024'] ? `$${row['2024'].toLocaleString()}` : "-"}</td>
                    <td className="growth-cell-modern">
                      {row.growth5y !== null && !isNaN(row.growth5y) ? (
                        <span className={`growth-pill-modern ${row.growth5y >= 0 ? "positive" : "negative"}`}>
                          {row.growth5y >= 0
                            ? (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 15 12 9 18 15"></polyline></svg>{Math.abs(row.growth5y).toFixed(2)}%</>)
                            : (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>{Math.abs(row.growth5y).toFixed(2)}%</>)
                          }
                        </span>
                      ) : "-"}
                    </td>
                    <td className="growth-cell-modern">
                      {row.growth10y !== null && !isNaN(row.growth10y) ? (
                        <span className={`growth-pill-modern ${row.growth10y >= 0 ? "positive" : "negative"}`}>
                          {row.growth10y >= 0
                            ? (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 15 12 9 18 15"></polyline></svg>{Math.abs(row.growth10y).toFixed(2)}%</>)
                            : (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>{Math.abs(row.growth10y).toFixed(2)}%</>)
                          }
                        </span>
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>

      {/* ===== EXPANDED CHART MODAL ===== */}
      {showChartModal && (
        <div className="mc-modal__overlay" onClick={onOverlayClick} role="dialog" aria-modal="true" aria-label="Expanded chart">
          <div className="mc-modal">
            <div className="mc-modal__header">
              <div className="mc-modal__title">
                <div className="mc-modal__title-main">Mean house price ($) per year</div>
                <div className="mc-modal__title-sub">Median house price over time by {isCity ? 'city' : 'state'}; shows how property values are trending.</div>
              </div>
              <button className="mc-modal__close" onClick={closeChartModal} aria-label="Close">✕</button>
            </div>

            <div className="mc-modal__body">
              {(yearsLoading || loadingChart) ? (
                <div className="chart-placeholder">Loading chart...</div>
              ) : (
                <ResponsiveContainer width="100%" height={520}>
                  <LineChart
                    data={chartSeries}
                    margin={{ top: 0.1, right: 28, left: 12, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" height={28} tickMargin={10} />
                    <YAxis
                      width={68}
                      tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)} mill.`}
                      ticks={yTicks}
                      domain={yTicks.length ? [yTicks[0], yTicks[yTicks.length - 1]] : ['auto', 'auto']}
                      interval="preserveStartEnd"
                      minTickGap={10}
                      padding={{ top: 16, bottom: 8 }}
                      tickMargin={10}
                      allowDecimals
                    />
                    <Tooltip
                      formatter={(v) => `$${Number(v).toLocaleString()}`}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Legend verticalAlign="top" align="left" height={46} />
                    {uniquePlaces.map((place) => (
                      <Line
                        key={place}
                        type="monotone"
                        dataKey={place}
                        stroke={colorMap[place]}
                        dot={false}
                        activeDot={{ r: 5 }}
                        strokeWidth={2}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
